import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AccountInfo } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useAuthStore, type User } from '../stores/authStore';
import { msalInstance, ensureMsalInitialized, loginScopes } from '../services/msalInstance';

/**
 * Hash a token string using SHA-256 (Web Crypto API).
 */
async function hashToken(token: string): Promise<string> {
  const data = new TextEncoder().encode(token);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function refreshBackendSession(accessToken: string, previousToken?: string | null): Promise<User | null> {
  try {
    const body: Record<string, string> = { accessToken };
    if (previousToken) {
      body.previousTokenHash = await hashToken(previousToken);
    }

    const response = await fetch('/api/auth/token-refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload?.success) {
      return null;
    }

    return normalizeUser(payload.user ?? payload.data?.user);
  } catch {
    return null;
  }
}

/** Proactive refresh interval — 45 minutes (well before the 1-hour MSAL token
 *  expiry and 8-hour backend session TTL), plus 1-5 minutes of random jitter
 *  to prevent thundering-herd across browser tabs / users. */
const PROACTIVE_REFRESH_BASE_MS = 45 * 60 * 1000;
const PROACTIVE_REFRESH_JITTER_MS = 4 * 60 * 1000; // 1-5 min range (base adds 1 min minimum)

function getProactiveRefreshInterval(): number {
  return PROACTIVE_REFRESH_BASE_MS + 60_000 + Math.floor(Math.random() * PROACTIVE_REFRESH_JITTER_MS);
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  /** Silently refresh MSAL token + backend session. Returns the new access
   *  token on success, null on failure (SSO truly expired). */
  refreshSession: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Authentication failed';
}

function normalizeUser(input: any): User {
  const roles = Array.isArray(input?.roles)
    ? input.roles
    : input?.role
      ? [input.role]
      : ['user'];

  return {
    id: String(input?.id ?? crypto.randomUUID()),
    name: String(input?.name ?? input?.displayName ?? input?.email ?? 'AFN User'),
    email: String(input?.email ?? ''),
    avatar: input?.avatar ?? undefined,
    roles: roles.map((role: unknown) => String(role).toUpperCase()),
  };
}

async function exchangeToken(accessToken: string): Promise<User> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ accessToken }),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok || !payload?.success || !payload?.user) {
    throw new Error(payload?.error || 'Microsoft sign-in failed');
  }

  return normalizeUser(payload.user);
}

async function acquireAccessToken(account: AccountInfo): Promise<string> {
  const result = await msalInstance.acquireTokenSilent({
    account,
    scopes: loginScopes,
  });

  return result.accessToken;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

function AuthProviderContent({ children }: AuthProviderProps) {
  const user = useAuthStore((state) => state.user);
  const isLoadingFromStore = useAuthStore((state) => state.isLoading);
  const loginWithToken = useAuthStore((state) => state.loginWithToken);
  const clearSession = useAuthStore((state) => state.clearSession);
  const setLoading = useAuthStore((state) => state.setLoading);

  const [error, setError] = useState<string | null>(null);

  const hydrateSession = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await ensureMsalInitialized();

      const redirectResult = await msalInstance.handleRedirectPromise();
      const account =
        redirectResult?.account ?? msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

      if (!account) {
        clearSession();
        return;
      }

      msalInstance.setActiveAccount(account);

      const accessToken = redirectResult?.accessToken || (await acquireAccessToken(account));
      const portalUser = await exchangeToken(accessToken);
      loginWithToken(accessToken, portalUser);
    } catch (error) {
      console.error('Authentication initialization failed:', error);
      clearSession();
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [clearSession, loginWithToken, setLoading]);

  useEffect(() => {
    void hydrateSession();
  }, [hydrateSession]);

  const login = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      await ensureMsalInitialized();
      await msalInstance.loginRedirect({
        scopes: loginScopes,
        prompt: 'select_account',
      });
    } catch (error) {
      console.error('Microsoft login failed:', error);
      setError(getErrorMessage(error));
      setLoading(false);
    }
  }, [setLoading]);

  const logout = useCallback(async () => {
    setError(null);
    clearSession();

    try {
      await ensureMsalInitialized();
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri:
          import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI ?? window.location.origin,
      });
    } catch (error) {
      console.error('Microsoft logout failed:', error);
      setError(getErrorMessage(error));
      setLoading(false);
    }
  }, [clearSession, setLoading]);

  const getAccessToken = useCallback(async () => {
    try {
      await ensureMsalInitialized();
      const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
      if (!account) {
        return null;
      }

      msalInstance.setActiveAccount(account);
      const accessToken = await acquireAccessToken(account);
      const currentUser = useAuthStore.getState().user;

      if (currentUser) {
        useAuthStore.getState().loginWithToken(accessToken, currentUser);
      }

      return accessToken;
    } catch (error) {
      console.error('Failed to acquire Microsoft access token:', error);
      return null;
    }
  }, []);

  /**
   * Silent session refresh: gets a fresh MSAL token and re-registers it on
   * the backend.  Returns the new access token on success, null if SSO is
   * truly gone.
   */
  const refreshSession = useCallback(async (): Promise<string | null> => {
    try {
      await ensureMsalInitialized();
      const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
      if (!account) {
        return null;
      }

      msalInstance.setActiveAccount(account);

      // Force-refresh the MSAL token (skip cache) to ensure we get a fresh one
      const result = await msalInstance.acquireTokenSilent({
        account,
        scopes: loginScopes,
        forceRefresh: true,
      });

      const accessToken = result.accessToken;
      if (!accessToken) {
        return null;
      }

      // Register the new token with the backend, passing previous token for cleanup
      const previousToken = useAuthStore.getState().token;
      const portalUser = await refreshBackendSession(accessToken, previousToken);
      if (!portalUser) {
        // Backend rejected the token — try full exchangeToken as fallback
        const exchangedUser = await exchangeToken(accessToken).catch(() => null);
        if (!exchangedUser) {
          return null;
        }
        loginWithToken(accessToken, exchangedUser);
        return accessToken;
      }

      loginWithToken(accessToken, portalUser);
      return accessToken;
    } catch (error) {
      console.error('Silent session refresh failed:', error);
      return null;
    }
  }, [loginWithToken]);

  // Proactive refresh timer: refresh the session before it expires
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Only start the timer when authenticated
    if (!user) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    // Schedule with jitter so multiple tabs / users don't all refresh at once
    const scheduleNext = () => {
      refreshTimerRef.current = setTimeout(() => {
        void refreshSession().finally(scheduleNext);
      }, getProactiveRefreshInterval());
    };
    scheduleNext();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [user, refreshSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading: isLoadingFromStore,
      error,
      login,
      logout,
      getAccessToken,
      refreshSession,
    }),
    [error, getAccessToken, isLoadingFromStore, login, logout, refreshSession, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderContent>{children}</AuthProviderContent>
    </MsalProvider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
